import { AioButtonComponent } from '@aio/ui';
import { Component, ChangeDetectionStrategy, computed, signal, inject, effect, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthFooterComponent } from '../footer/auth-footer.component';
import { pickRandomAuthHero } from '../auth-hero.util';
import { AuthHeroPanelComponent } from '../auth-hero-panel/auth-hero-panel.component';
import { AuthService } from '../../../core/services/auth.service';
import { ValidateTokenRequest, ResetPasswordRequest } from '../../../core/models/auth.models';

@Component({
  selector: 'app-setnewpassword',
  standalone: true,
  imports: [
    AioButtonComponent,CommonModule, ReactiveFormsModule, AuthFooterComponent, AuthHeroPanelComponent],
  templateUrl: './setnewpassword.component.html',
  styleUrls: ['./setnewpassword.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetNewPasswordComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  readonly hero = pickRandomAuthHero();

  // Signals for reactive state management
  readonly passwordVisible = signal(false);
  readonly confirmPasswordVisible = signal(false);
  readonly showSpecialCharError = signal(false);
  readonly passwordValue = signal('');
  readonly confirmPasswordValue = signal('');
  readonly loading = signal(false);
  readonly tokenValid = signal(false);
  readonly tokenError = signal('');
  readonly submitError = signal('');
  readonly submitSuccess = signal(false);
  readonly currentPasswordFromTokenValue = signal('');

  // Reactive form
  readonly passwordForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]]
  });

  // Computed properties
  readonly password = computed(() => this.passwordValue());
  readonly confirmPassword = computed(() => this.confirmPasswordValue());
  readonly currentPasswordFromToken = computed(() => this.currentPasswordFromTokenValue());
  
  readonly hasSpecialCharacter = computed(() => {
    const password = this.password();
    return /[!@#$%^&*(),.?":{}|<>]/.test(password);
  });

  readonly hasUppercaseLetter = computed(() => {
    const password = this.password();
    return /[A-Z]/.test(password);
  });

  readonly hasMinLength = computed(() => this.password().length >= 8);

  readonly hasMixedCase = computed(() => {
    const password = this.password();
    return /[a-z]/.test(password) && /[A-Z]/.test(password);
  });

  readonly isPasswordError = computed(() => 
    this.showSpecialCharError() && (!this.hasSpecialCharacter() || !this.hasUppercaseLetter()) ||
    this.showPasswordRepeatError()
  );

  readonly isConfirmPasswordError = computed(() => 
    this.showSpecialCharError() && (!this.hasSpecialCharacter() || !this.hasUppercaseLetter())
  );

  readonly canSubmit = computed(() => {
    const password = this.password();
    const confirmPassword = this.confirmPassword();
    const currentPassword = this.currentPasswordFromToken();
    const hasValidPassword = password && password.length >= 8 && this.hasSpecialCharacter() && this.hasUppercaseLetter();
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
    // Check password difference - if current password is available, ensure it's different
    const isPasswordDifferent = currentPassword ? (password && password !== currentPassword) : true;
    
    const canSubmit = this.tokenValid() && 
           !this.loading() && 
           hasValidPassword && 
           passwordsMatch &&
           isPasswordDifferent;

    return canSubmit;
  });

  readonly showPasswordRepeatError = computed(() => {
    const password = this.password();
    const currentPassword = this.currentPasswordFromToken();
    return currentPassword && password && password === currentPassword;
  });

  private resetToken = '';

  constructor() {
    // Listen to password field changes
    this.passwordForm.get('password')?.valueChanges.subscribe(value => {
      this.passwordValue.set(value || '');
    });
    
    // Listen to confirm password field changes
    this.passwordForm.get('confirmPassword')?.valueChanges.subscribe(value => {
      this.confirmPasswordValue.set(value || '');
    });
  }

  ngOnInit(): void {
    // Get reset token from URL query params
    this.route.queryParams.subscribe(params => {
      this.resetToken = params['resetToken'] || '';
      if (this.resetToken) {
        this.validateResetToken();
      } else {
        this.tokenError.set('No reset token provided. Please request a new password reset link.');
      }
    });
  }

  private validateResetToken(): void {
    this.loading.set(true);
    this.tokenError.set('');

    const request: ValidateTokenRequest = {
      resetToken: this.resetToken
    };

    this.authService.validateResetToken(request).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.tokenValid.set(true);
          // Store current password from token validation for password history check
          if (response.data?.currentPassword) {
            this.currentPasswordFromTokenValue.set(response.data.currentPassword);
          }
        } else {
          this.tokenError.set(response.message || 'Invalid or expired reset token.');
        }
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Token validation error:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        console.error('Error response:', error.error);
        
        const errorMessage = error.error?.message || 'Failed to validate reset token.';
        this.tokenError.set(errorMessage);
        
        // If token is expired, show specific message
        if (errorMessage.toLowerCase().includes('expired')) {
          this.tokenError.set('This reset link has expired. Please request a new password reset link.');
        }
      }
    });
  }

  onPasswordChange(): void {
    // Hide error when user starts typing and has special character and uppercase
    if (this.hasSpecialCharacter() && this.hasUppercaseLetter()) {
      this.showSpecialCharError.set(false);
    }
    
    // Clear repeat error when user starts typing a different password
    const password = this.password();
    const currentPassword = this.currentPasswordFromToken();
    if (password && currentPassword && password !== currentPassword) {
      // Password is now different, so no need to show repeat error
    }
  }

  onPasswordBlur(): void {
    // Show error if missing special character or uppercase when leaving the field
    if (!this.hasSpecialCharacter() || !this.hasUppercaseLetter()) {
      this.showSpecialCharError.set(true);
    }
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }

    // Additional check to prevent API call if password is the same as current
    const currentPassword = this.currentPasswordFromToken();
    const newPassword = this.password();
    if (currentPassword && newPassword && currentPassword === newPassword) {
      this.submitError.set('You cannot repeat a password you\'ve used previously');
      return;
    }

    this.loading.set(true);
    this.submitError.set('');

    const request: ResetPasswordRequest = {
      resetToken: this.resetToken,
      newPassword: this.password(),
      confirmNewPassword: this.confirmPassword(),
      currentPassword: this.currentPasswordFromToken()
    };

    this.authService.submitNewPassword(request).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.submitSuccess.set(true);
          // Navigate to reset success page
          this.router.navigate(['/reset-success']);
        } else {
          this.submitError.set(response.message || 'Failed to reset password. Please try again.');
        }
      },
      error: (error) => {
        this.loading.set(false);
        const errorMessage = error.error?.message || 'An error occurred while resetting your password.';
        this.submitError.set(errorMessage);
        
        // If token is expired, show specific message
        if (errorMessage.toLowerCase().includes('expired')) {
          this.submitError.set('This reset link has expired. Please request a new password reset link.');
        }
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/login']);
  }

  requestNewLink(): void {
    this.router.navigate(['/forgot-password']);
  }
} 